import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Link from "next/link";
import { twMerge } from "tailwind-merge";

import { OverflowTooltip } from "@/components/overflow-tooltip";
import { links } from "@/data/links";
import { useLang } from "@/hooks/use-lang";
import { toTitleCase } from "@/utils/to-title-case";

import { Top20TableDetail } from "../services/get-investor-top20-table";

export const useTop20CostEstimationTable = ({
  data,
}: {
  data: Top20TableDetail[];
}) => {
  const { t, lang } = useLang();
  const columnHelper = createColumnHelper<Top20TableDetail>();
  const columns = [
    columnHelper.accessor("symbol", {
      header: () => (
        <Header className="whitespace-nowrap pl-2">{t.my13F.symbol}</Header>
      ),
      cell: (info) => (
        <Link
          target="_blank"
          className="block w-full whitespace-nowrap pl-2 font-medium text-sky-700 underline underline-offset-2"
          href={`/${lang}${links.myAnalysisOverview({
            symbol: info.getValue() ?? "",
          })}`}
        >
          {info.getValue()}
        </Link>
      ),
      size: 100,
    }),
    columnHelper.accessor("company", {
      header: () => (
        <Header className="max-w-[140px] whitespace-nowrap">
          {t.my13F.company}
        </Header>
      ),
      cell: (info) => (
        <OverflowTooltip
          wrapperClassName="max-w-[140px] font-normal"
          title={toTitleCase(info.getValue() ?? "-")}
        >
          {toTitleCase(info.getValue() ?? "-")}
        </OverflowTooltip>
      ),
    }),
    columnHelper.accessor("weightPct", {
      header: () => (
        <Header className="whitespace-nowrap">
          {t.my13F.investor.proportion}
        </Header>
      ),
      cell: (info) => {
        const value = info.getValue();
        return (
          <p className="text-sm font-medium">
            {value ? `${+value?.toFixed(2)}%` : "-"}
          </p>
        );
      },
      meta: { align: "right" },
    }),
    columnHelper.accessor("costPrice", {
      header: () => (
        <Header className="whitespace-nowrap">
          {t.my13F.investor.estimatedCost}
        </Header>
      ),
      cell: (info) => {
        const value = info.getValue();
        const displayValue = value
          ? `$${+value.toFixed(2).toLocaleString()}`
          : "-";
        return <p className="text-sm font-medium">{displayValue}</p>;
      },
      meta: { align: "right" },
    }),
    columnHelper.accessor("closePrice", {
      header: () => (
        <Header className="whitespace-nowrap">{t.analysis.shared.price}</Header>
      ),
      cell: (info) => {
        const value = info.getValue();
        const displayValue = value
          ? `$${+value.toFixed(2).toLocaleString()}`
          : "-";
        return <p className="text-sm font-medium">{displayValue}</p>;
      },
      meta: { align: "right" },
    }),
    columnHelper.accessor("estimatedPnl", {
      header: () => (
        <Header className="whitespace-nowrap pr-2">
          {t.my13F.investor.estimatedPAndL}
        </Header>
      ),
      cell: (info) => {
        const value = info.getValue();
        if (value == null) return <p className="pr-2 text-sm font-medium">-</p>;

        const isRise = value > 0;
        const isFall = value < 0;

        return (
          <div className="flex w-full items-center justify-end gap-1 px-1">
            <p
              className={twMerge(
                "text-sm font-medium text-[#202020]",
                isRise && "text-emerald-500",
                isFall && "text-red-500"
              )}
            >
              {+(value * 100).toFixed(2)}%
            </p>
          </div>
        );
      },
      meta: { align: "right" },
    }),
  ];

  return useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
  });
};

const Header = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={twMerge("font-medium text-slate-500", className)}>
    {children}
  </div>
);
